import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faWeightScale,
  faDumbbell,
  faHeart,
  faArrowLeft,
  faArrowRight,
  faCheck,
  faPersonWalking,
  faPersonRunning,
  faCouch,
  faDumbbell as faDumbbellSolid,
  faClock,
  faStopwatch,
  faHourglassHalf,
  faCalendarDay,
  faLeaf,
  faFish,
  faDrumstickBite,
  faWheatAwn,
  faBan,
  faEgg,
  faRuler,
  faTape
} from '@fortawesome/free-solid-svg-icons'
import '../styling/PersonalCareForm.css'
import { useSelector } from 'react-redux'
import { getPatientData } from '../../../services/personalCareService'
import { Alert } from 'antd'
import { saveFormProgress, getFormProgress } from '../../../services/storageService'

const steps = [
  {
    id: 'goals',
    title: 'What is your main goal?',
    options: [
      { id: 'weight-loss', icon: faWeightScale, label: 'Weight Loss', description: 'Lose weight and burn fat' },
      { id: 'muscle-gain', icon: faDumbbell, label: 'Build Muscle', description: 'Gain strength and muscle mass' },
      { id: 'general-health', icon: faHeart, label: 'General Health', description: 'Improve overall wellness' }
    ]
  },
  {
    id: 'activity',
    title: 'What is your activity level?',
    options: [
      {
        id: 'sedentary',
        icon: faCouch,
        label: 'Sedentary',
        description: 'Little to no exercise'
      },
      {
        id: 'moderate',
        icon: faPersonWalking,
        label: 'Moderate',
        description: 'Exercise 3-5 times a week'
      },
      {
        id: 'active',
        icon: faPersonRunning,
        label: 'Very Active',
        description: 'Exercise 6-7 times a week'
      },
      {
        id: 'athlete',
        icon: faDumbbellSolid,
        label: 'Athlete',
        description: 'Multiple training sessions daily'
      }
    ]
  },
  {
    id: 'availability',
    title: 'How much time can you commit daily?',
    options: [
      {
        id: 'minimal',
        icon: faClock,
        label: '15-30 Minutes',
        description: 'Quick, effective workouts'
      },
      {
        id: 'moderate',
        icon: faStopwatch,
        label: '30-60 Minutes',
        description: 'Standard workout duration'
      },
      {
        id: 'flexible',
        icon: faHourglassHalf,
        label: '1-2 Hours',
        description: 'Extended training sessions'
      },
      {
        id: 'dedicated',
        icon: faCalendarDay,
        label: '2+ Hours',
        description: 'Multiple sessions per day'
      }
    ]
  },
  {
    id: 'dietaryPreferences',
    title: 'Any dietary preferences?',
    options: [
      {
        id: 'vegetarian',
        icon: faLeaf,
        label: 'Vegetarian',
        description: 'Plant-based, no meat'
      },
      {
        id: 'pescatarian',
        icon: faFish,
        label: 'Pescatarian',
        description: 'Fish, no other meat'
      },
      {
        id: 'keto',
        icon: faDrumstickBite,
        label: 'Keto',
        description: 'Low-carb, high-fat'
      },
      {
        id: 'gluten-free',
        icon: faWheatAwn,
        label: 'Gluten Free',
        description: 'No wheat products'
      },
      {
        id: 'dairy-free',
        icon: faBan,
        label: 'Dairy Free',
        description: 'No dairy products'
      },
      {
        id: 'paleo',
        icon: faEgg,
        label: 'Paleo',
        description: 'Whole foods based'
      }
    ]
  },
  {
    id: 'measurements',
    title: 'Physical Measurements',
    type: 'measurements',
    fields: [
      {
        id: 'height',
        label: 'Height',
        unit: 'cm',
        icon: faRuler,
        placeholder: '175'
      },
      {
        id: 'weight',
        label: 'Weight',
        unit: 'kg',
        icon: faWeightScale,
        placeholder: '70'
      },
      {
        id: 'waist',
        label: 'Waist',
        unit: 'cm',
        icon: faTape,
        placeholder: '82'
      }
    ]
  }
]

export const PersonalCareForm = ({ onSubmit, onSubmitStart, setIsLoading }) => {
  const authState = useSelector((state) => state.auth)
  const userRef = authState?.agiliteUser?._id
  const [currentStep, setCurrentStep] = useState(0)
  const [formData, setFormData] = useState({
    goals: [],
    activity: '',
    availability: '',
    dietaryPreferences: [],
    measurements: {
      height: '',
      weight: '',
      waist: ''
    }
  })
  const [initializing, setInitializing] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  useEffect(() => {
    const checkExistingData = async () => {
      try {
        // Placeholder for API call to get patient data
        const patientData = await getPatientData(userRef)

        // Auto-fill form if data exists
        if (patientData) {
          setFormData((prev) => ({
            ...prev,
            height: patientData.height || '',
            weight: patientData.weight || '',
            waist: patientData.waist || '',
            goals: patientData.goals || [],
            activity: patientData.activityLevel || '',
            availability: patientData.availability || '',
            dietaryPreferences: patientData.dietaryPreferences || []
          }))
        }
      } catch (error) {
        console.error('Error fetching patient data:', error)
      }
      setInitializing(false)
    }

    checkExistingData()
  }, [userRef])

  useEffect(() => {
    const savedProgress = getFormProgress()
    if (savedProgress) {
      setFormData(savedProgress)
    }
  }, [])

  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      saveFormProgress(formData)
    }
  }, [formData])

  const handleSelect = (field, value) => {
    setFormData((prev) => {
      // For activity level and availability, we only want one selection
      if (field === 'activity' || field === 'availability') {
        return {
          ...prev,
          [field]: value
        }
      }

      // For goals, we allow multiple selections
      const values = prev[field]
      if (values.includes(value)) {
        return {
          ...prev,
          [field]: values.filter((v) => v !== value)
        }
      } else {
        return {
          ...prev,
          [field]: [...values, value]
        }
      }
    })
  }

  const validateMeasurements = () => {
    const { height, weight, waist } = formData.measurements
    return height && weight && waist && parseFloat(height) > 0 && parseFloat(weight) > 0 && parseFloat(waist) > 0
  }

  const canProceed = () => {
    const currentStepId = steps[currentStep].id
    if (currentStepId === 'measurements') {
      return validateMeasurements()
    }
    if (currentStepId === 'goals') {
      return formData.goals.length > 0
    }
    if (currentStepId === 'activity') {
      return formData.activity !== ''
    }
    if (currentStepId === 'availability') {
      return formData.availability !== ''
    }
    return true
  }

  const handleSubmit = async () => {
    if (isSubmitting) {
      return
    }

    setIsSubmitting(true)
    try {
      const submissionData = {
        goals: formData.goals,
        activity: formData.activity,
        availability: formData.availability,
        dietaryPreferences: formData.dietaryPreferences,
        measurements: {
          height: parseFloat(formData.measurements.height),
          weight: parseFloat(formData.measurements.weight),
          waist: parseFloat(formData.measurements.waist)
        }
      }

      await onSubmit(submissionData)
    } catch (error) {
      console.error('Error in handleSubmit:', error)
      setSubmitError('Failed to submit form. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleNext = () => {
    if (!canProceed()) {
      return
    }

    // Only submit on measurements step
    if (steps[currentStep].id === 'measurements' && validateMeasurements()) {
      if (!isSubmitting) {
        handleSubmit()
      }
    } else if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1)
    }
  }

  const isOptionSelected = (stepId, optionId) => {
    if (stepId === 'goals' || stepId === 'dietaryPreferences') {
      return formData[stepId].includes(optionId)
    }
    if (stepId === 'activity' || stepId === 'availability') {
      return formData[stepId] === optionId
    }
    return false
  }

  const handleMeasurementChange = (field, value) => {
    const numValue = parseFloat(value)
    if (!isNaN(numValue)) {
      setFormData((prev) => {
        const newData = {
          ...prev,
          measurements: {
            ...prev.measurements,
            [field]: numValue
          }
        }

        return newData
      })
    }
  }

  return (
    <div className='personal-care-form' style={{}}>
      {/* Progress bar */}
      <div className='form-progress'>
        <div className='progress-bar' style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }} />
      </div>

      {!isSubmitting ? (
        <>
          {' '}
          <AnimatePresence mode='wait'>
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className='step-content'
            >
              <h2>{steps[currentStep].title}</h2>

              {steps[currentStep].type === 'measurements' ? (
                // Render measurement inputs
                <div className='measurements-grid'>
                  {steps[currentStep].fields.map((field) => (
                    <div key={field.id} className='measurement-input'>
                      <div className='measurement-icon'>
                        <FontAwesomeIcon icon={field.icon} />
                      </div>
                      <div className='measurement-content'>
                        <span className='measurement-label'>{field.label}</span>
                        <input
                          type='number'
                          placeholder={field.placeholder}
                          value={formData.measurements[field.id]}
                          onChange={(e) => handleMeasurementChange(field.id, e.target.value)}
                        />
                      </div>
                      <span className='measurement-unit'>{field.unit}</span>
                    </div>
                  ))}
                </div>
              ) : (
                // Render option cards for other steps
                <div className='options-grid' data-step={steps[currentStep].id}>
                  {steps[currentStep].options.map((option) => (
                    <motion.div
                      key={option.id}
                      className={`option-card ${isOptionSelected(steps[currentStep].id, option.id) ? 'selected' : ''}`}
                      onClick={() => handleSelect(steps[currentStep].id, option.id)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className='option-icon'>
                        <FontAwesomeIcon icon={option.icon} />
                      </div>
                      <div className='option-content'>
                        <h3>{option.label}</h3>
                        <p>{option.description}</p>
                      </div>
                      <div className='option-checkbox'>
                        {isOptionSelected(steps[currentStep].id, option.id) && (
                          <motion.div className='check-icon' initial={{ scale: 0 }} animate={{ scale: 1 }}>
                            <FontAwesomeIcon icon={faCheck} />
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </>
      ) : undefined}

      {submitError && (
        <Alert
          message='Error'
          description={submitError}
          type='error'
          showIcon
          closable
          onClose={() => setSubmitError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Navigation */}
      <div className='form-navigation'>
        {currentStep > 0 && (
          <motion.button
            className='nav-button back'
            onClick={handleBack}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Back
          </motion.button>
        )}
        <motion.button
          className={`nav-button next ${!canProceed() ? 'disabled' : ''}`}
          onClick={handleNext}
          whileHover={canProceed() ? { scale: 1.05 } : {}}
          whileTap={canProceed() ? { scale: 0.95 } : {}}
        >
          Next
          <FontAwesomeIcon icon={faArrowRight} />
        </motion.button>
      </div>
    </div>
  )
}
